import type { ZElement } from "../../arrangement/ZElement";
import { I18N } from "oc-I18N";
import { erzeugeSchiebetorRahmen, schiebetorKennzahlen } from "./erzeugeSchiebetorRahmen";
import { generateFrame } from "./frameGenerator";
import { v3 } from "oc-threejs/short3";

const MM = 1000;
const DIM_COLOR = "#555";

function mToMmText(valueM: number) {
  return `${Math.round(valueM * MM)} mm`;
}

function buildSketchHtml(
  tor: ZElement,
  k: ReturnType<typeof schiebetorKennzahlen>,
  forOverlay = false
) {
  const configurationCode = (APP as any)?.configuration?.Code ?? "-";
  const datum = new Date().toLocaleDateString("de-DE");
  const fGrad = (k.F * 180) / Math.PI;
  const rahmen = erzeugeSchiebetorRahmen({
    torHöhe: tor.size.y,
    lichteWeite: tor.lichteWeite,
    schiebetorFlügelMaxBreite: tor.meta?.Mass?.SchiebetorFlügelMaxBreite ?? 1.86,
    gespiegeltZ: tor.schiebetormotorseiteAuswahlwert?.Key === "Schiebetormotorseite_B",
  });
  const eFensterBreite = rahmen.füllungsFenster?.[0]
    ? rahmen.füllungsFenster[0].max.x - rahmen.füllungsFenster[0].min.x
    : null;
  const eFensterBreiteText = eFensterBreite != null ? `${Math.round(eFensterBreite * MM)} mm` : "-";
  const closeButton = forOverlay
    ? `<button id="closeSchiebetorSkizze" style="float:right;padding:6px 10px;border:1px solid #999;background:#fff;cursor:pointer;">${I18N("Schliessen")}</button>`
    : "";

  return `
    <style>
      body { font-family: Arial, sans-serif; margin: 16px; color: #222; }
      h2 { margin: 0 0 12px 0; font-size: 18px; }
      .tables { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
      table { border-collapse: collapse; width: 100%; font-size: 13px; }
      th, td { border: 1px solid #bbb; padding: 6px 8px; }
      th { background: #f3f3f3; text-align: left; }
      .canvasWrap { border: 1px solid #bbb; padding: 8px; }
      .meta { margin: 0 0 8px 0; font-size: 13px; color: #555; }
    </style>
    ${closeButton}
    <h2>${I18N("Schiebetor-Skizze")}</h2>
    <p class="meta">${I18N("Element")} #${tor.elementNr + 1}</p>
    <div class="tables">
      <table>
        <thead><tr><th colspan="3">${I18N("Tabelle 1")}</th></tr></thead>
        <tbody>
          <tr><td>${I18N("Code")}:</td><td colspan="2">${configurationCode}</td></tr>
          <tr><td>${I18N("Lichte Weite in mm")}:</td><td colspan="2">${Math.round(k.L1 * MM)}</td></tr>
          <tr><td>${I18N("Torhöhe in mm")}:</td><td colspan="2">${Math.round(tor.size.y * MM)}</td></tr>
          <tr><td>${I18N("Datum")}:</td><td colspan="2">${datum}</td></tr>
          <tr><td>${I18N("Lfd. Nr.")}:</td><td colspan="2">1</td></tr>
        </tbody>
      </table>
      <table>
        <thead><tr><th colspan="3">${I18N("Tabelle 2")}</th></tr></thead>
        <tbody>
          <tr><td>A</td><td>LT</td><td>${mToMmText(k.A)}</td></tr>
          <tr><td>B</td><td>${I18N("Höhe")} + 10 mm</td><td>${mToMmText(k.B)}</td></tr>
          <tr><td>C</td><td>L1 - 40 + 185 mm</td><td>${mToMmText(k.C)}</td></tr>
          <tr><td>D</td><td>A - C (${I18N("knickDeltaX")})</td><td>${mToMmText(k.D)}</td></tr>
          <tr><td>E</td><td>${I18N("Fensterbreite")}</td><td>${eFensterBreiteText}</td></tr>
          <tr><td>F</td><td>${I18N("Winkel")}</td><td>${Math.round(fGrad)} °</td></tr>
          <tr><td>G</td><td>${I18N("Abstand Knick zur ersten Strebe")}</td><td>${mToMmText(k.G)}</td></tr>
        </tbody>
      </table>
    </div>
    <div class="canvasWrap">
      <canvas id="schiebetorSketch" width="1400" height="640"></canvas>
    </div>
  `;
}

function drawDimLine(
  ctx: CanvasRenderingContext2D,
  from: { x: number; y: number },
  to: { x: number; y: number },
  label: string,
  options?: {
    gapPx?: number;
    labelOffsetPx?: { x?: number; y?: number };
    labelAlign?: CanvasTextAlign;
  }
) {
  ctx.save();
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy);
  if (len <= 0.00001) {
    return;
  }
  const ux = dx / len;
  const uy = dy / len;
  const px = -uy;
  const py = ux;
  const arrowLen = 10;
  const arrowHalfWidth = 3.6;

  // Feste Label-Lücke: bei eher horizontalen Linien basierend auf der Textbreite,
  // bei eher vertikalen Linien reicht die Font-Höhe.
  const fontPxMatch = /\b(\d+(?:\.\d+)?)px\b/.exec(ctx.font ?? "");
  const fontPx = fontPxMatch ? parseFloat(fontPxMatch[1]) : 14;
  const textWidth = Math.max(1, ctx.measureText(label).width);
  const isMoreVertical = Math.abs(dy) > Math.abs(dx);
  const autoGap = isMoreVertical ? fontPx * 3 : Math.max(textWidth * 3, fontPx * 3);
  const gapLenRaw = options?.gapPx ?? autoGap;
  const gapLen = Math.min(gapLenRaw, len * 0.85);
  const segmentLen = (len - gapLen) / 2;
  const p1 = { x: from.x + ux * segmentLen, y: from.y + uy * segmentLen };
  const p2 = { x: to.x - ux * segmentLen, y: to.y - uy * segmentLen };

  ctx.strokeStyle = DIM_COLOR;
  ctx.fillStyle = DIM_COLOR;

  ctx.beginPath();
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(p1.x, p1.y);
  ctx.moveTo(p2.x, p2.y);
  ctx.lineTo(to.x, to.y);
  ctx.stroke();

  const drawArrow = (p: { x: number; y: number }, dirX: number, dirY: number) => {
    const bx = p.x + dirX * arrowLen;
    const by = p.y + dirY * arrowLen;
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    ctx.lineTo(bx + px * arrowHalfWidth, by + py * arrowHalfWidth);
    ctx.lineTo(bx - px * arrowHalfWidth, by - py * arrowHalfWidth);
    ctx.closePath();
    ctx.fill();
  };

  // Pfeile zeigen nach innen auf die Maßlinie.
  drawArrow(from, ux, uy);
  drawArrow(to, -ux, -uy);

  const mx = (from.x + to.x) * 0.5 + (options?.labelOffsetPx?.x ?? 0);
  const my = (from.y + to.y) * 0.5 + (options?.labelOffsetPx?.y ?? 0);
  ctx.save();
  ctx.textAlign = options?.labelAlign ?? "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#c00000";
  ctx.fillText(label, mx, my);
  ctx.restore();
}

function drawAngleArcBySweep(
  ctx: CanvasRenderingContext2D,
  center: { x: number; y: number },
  radius: number,
  startAngle: number,
  sweepAngle: number
) {
  const steps = 28;
  ctx.beginPath();
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const a = startAngle + sweepAngle * t;
    const x = center.x + Math.cos(a) * radius;
    const y = center.y + Math.sin(a) * radius;
    if (i === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  }
  ctx.stroke();
}

function drawSchiebetorCanvas(canvas: HTMLCanvasElement, tor: ZElement) {
  const torHöhe = tor.size.y;
  const lichteWeite = tor.lichteWeite;
  const schiebetorFlügelMaxBreite = tor.meta?.Mass?.SchiebetorFlügelMaxBreite ?? 1.86;
  const gespiegeltZ = tor.schiebetormotorseiteAuswahlwert?.Key === "Schiebetormotorseite_B";

  const rahmen = erzeugeSchiebetorRahmen({
    torHöhe,
    lichteWeite,
    schiebetorFlügelMaxBreite,
    gespiegeltZ,
  });

  const polygonKanten = generateFrame(rahmen);
  const skizzeAusblenden = new Set([
    "profilVertical",
    "profilHorizontal",
    "SchiebetorGummi",
    "FührungsschieneSeite",
  ]);
  const sichtbarePolygonKanten = polygonKanten.filter(
    (pk) => !pk.kante?.name || !skizzeAusblenden.has(pk.kante.name)
  );
  const polygons = sichtbarePolygonKanten.map((pk) => pk.polygon);
  const allPolyPoints = polygons.flat();

  const findEdge = (...names: string[]) =>
    polygonKanten.find((pk) => pk.kante?.name != null && names.includes(pk.kante.name));
  const outerTop = findEdge("AussenrahmenOben");
  const outerBottom = findEdge("AussenrahmenUnten", "FührungsschieneUnten");
  const schraegRechts = findEdge("schrägRechts", "AussenrahmenSchraegRechts");
  const strebeRechtsVorDreieck = findEdge("StrebeRechtsVorDreieck");

  const topPts = [outerTop?.kante.start.position, outerTop?.kante.end.position].filter(
    (p) => p != null
  );
  const bottomPts = [outerBottom?.kante.start.position, outerBottom?.kante.end.position].filter(
    (p) => p != null
  );
  const schraegPts = [
    schraegRechts?.kante.start.position,
    schraegRechts?.kante.end.position,
  ].filter((p) => p != null);
  const pLinksOben =
    topPts.length >= 2 ? topPts.toSorted((a, b) => a.x - b.x)[0] : { x: 0, y: torHöhe };
  const pKnickOben =
    topPts.length >= 2
      ? topPts.toSorted((a, b) => b.x - a.x)[0]
      : { x: rahmen.torBreite - rahmen.dreiecksBreite, y: torHöhe };
  const pLinksUnten =
    bottomPts.length >= 2 ? bottomPts.toSorted((a, b) => a.x - b.x)[0] : { x: 0, y: 0 };
  const pRechtsUnten =
    bottomPts.length >= 2
      ? bottomPts.toSorted((a, b) => b.x - a.x)[0]
      : { x: rahmen.torBreite, y: 0 };
  const pKnickRechts =
    schraegPts.length >= 2
      ? schraegPts.toSorted((a, b) => b.x - a.x)[0]
      : { x: rahmen.torBreite, y: 0 };

  const minX = Math.min(
    ...allPolyPoints.map((p) => p.x),
    ...rahmen.knoten.map((k) => k.position.x),
    0
  );
  const minY = Math.min(...allPolyPoints.map((p) => p.y), 0);

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas 2D context konnte nicht erzeugt werden");
  }

  const padLeft = 60;
  const padRight = 0;
  const padTop = 100;
  const padBottom = 60;

  const w = canvas.width - padLeft - padRight;
  const h = canvas.height - padTop - padBottom;
  // Feste Skala basierend auf maximaler Schiebetor-Größe (5.98m × 1.8m),
  // damit alle Tore maßstabsgerecht dargestellt werden.
  const maxTorBreiteM = 5.98;
  const maxTorHöheM = 1.8;
  const scale = Math.min(w / maxTorBreiteM, h / maxTorHöheM);

  const sx = (x: number) => padLeft + (x - minX) * scale;
  const sy = (y: number) => canvas.height - padBottom - (y - minY) * scale;

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.strokeStyle = "#111";
  ctx.lineWidth = 1.6;
  ctx.lineCap = "round";

  // Rahmen als echte Polygone zeichnen (anstatt nur Mittellinien der Kanten).
  ctx.fillStyle = "#d9d9d9";
  for (const poly of polygons) {
    if (poly.length < 3) {
      continue;
    }
    ctx.beginPath();
    ctx.moveTo(sx(poly[0].x), sy(poly[0].y));
    for (let i = 1; i < poly.length; i++) {
      ctx.lineTo(sx(poly[i].x), sy(poly[i].y));
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }

  const k = schiebetorKennzahlen({ lichteWeite, torHöhe });

  ctx.font = "bold 24px Arial";
  ctx.fillStyle = DIM_COLOR;
  ctx.strokeStyle = DIM_COLOR;

  // Höhe links
  const leftX = sx(pLinksUnten.x) - 45;
  ctx.beginPath();
  ctx.moveTo(sx(pLinksUnten.x), sy(pLinksUnten.y));
  ctx.lineTo(leftX, sy(0));
  ctx.moveTo(sx(pLinksOben.x), sy(pLinksOben.y));
  ctx.lineTo(leftX, sy(pLinksOben.y));
  ctx.stroke();
  drawDimLine(ctx, { x: leftX, y: sy(pLinksUnten.y) }, { x: leftX, y: sy(pLinksOben.y) }, "B");

  // C oben
  const yDim1 = Math.max(sy(pLinksOben.y) - 26, 18);
  ctx.beginPath();
  ctx.moveTo(sx(pLinksOben.x), sy(pLinksOben.y));
  ctx.lineTo(sx(pLinksOben.x), yDim1);
  ctx.moveTo(sx(pKnickOben.x), sy(pKnickOben.y));
  ctx.lineTo(sx(pKnickOben.x), yDim1);
  ctx.stroke();
  drawDimLine(ctx, { x: sx(pLinksOben.x), y: yDim1 }, { x: sx(pKnickOben.x), y: yDim1 }, "C");

  // A unten (LT)
  const yDim2 = sy(0) + 50;
  ctx.beginPath();
  ctx.moveTo(sx(pLinksUnten.x), sy(pLinksUnten.y));
  ctx.lineTo(sx(pLinksUnten.x), yDim2);
  ctx.moveTo(sx(pRechtsUnten.x), sy(pRechtsUnten.y));
  ctx.lineTo(sx(pRechtsUnten.x), yDim2);
  ctx.stroke();
  drawDimLine(ctx, { x: sx(pLinksUnten.x), y: yDim2 }, { x: sx(pRechtsUnten.x), y: yDim2 }, "A");

  // D oben (Dreiecks-/Knickbereich)
  const yDim3 = Math.max(sy(pKnickOben.y) - 52, 18);
  ctx.beginPath();
  ctx.moveTo(sx(pKnickOben.x), sy(pKnickOben.y));
  ctx.lineTo(sx(pKnickOben.x), yDim3);
  ctx.moveTo(sx(pKnickRechts.x), sy(pKnickRechts.y));
  ctx.lineTo(sx(pKnickRechts.x), yDim3);
  ctx.stroke();
  drawDimLine(ctx, { x: sx(pKnickOben.x), y: yDim3 }, { x: sx(pKnickRechts.x), y: yDim3 }, "D");

  // Zusatzmaß wie in der Originalskizze: Abstand Knick zur rechten Kante der inneren Vertikalstrebe (G).
  const xKnickZurStrebe = strebeRechtsVorDreieck
    ? Math.max(...strebeRechtsVorDreieck.polygon.map((p) => p.x))
    : pKnickOben.x - k.abstandKnickObenErsteStrebe;
  const yDimKnick = Math.max(sy(pKnickOben.y) - 78, 18);
  ctx.beginPath();
  ctx.moveTo(sx(pKnickOben.x), sy(pKnickOben.y));
  ctx.lineTo(sx(pKnickOben.x), yDimKnick);
  ctx.moveTo(sx(xKnickZurStrebe), sy(pKnickOben.y));
  ctx.lineTo(sx(xKnickZurStrebe), yDimKnick);
  ctx.stroke();
  // Bei kleinen Maßen eine kleinere Label-Lücke, damit die Breite visuell klarer bleibt.
  drawDimLine(
    ctx,
    { x: sx(xKnickZurStrebe), y: yDimKnick },
    { x: sx(pKnickOben.x), y: yDimKnick },
    "G",
    {
      gapPx: 30,
    }
  );

  // E in jedem Fenster als Maßlinie mit Pfeilen.
  for (const fenster of rahmen.füllungsFenster ?? []) {
    const yE = fenster.min.y + (fenster.max.y - fenster.min.y) * 0.5;
    drawDimLine(ctx, { x: sx(fenster.min.x), y: sy(yE) }, { x: sx(fenster.max.x), y: sy(yE) }, "E");
  }

  // F im inneren unteren Dreieckspunkt: nur Winkelbogen ohne Schenkel.

  const schrägRechts = findEdge("schrägRechts", "AussenrahmenSchraegRechts");
  const pkCenterPosition = schrägRechts?.polygon?.[0] ?? pKnickRechts;
  const pKCenter = { x: sx(pkCenterPosition.x), y: sy(pkCenterPosition.y) };
  const aLeft = Math.PI;
  const r = 58;

  drawAngleArcBySweep(ctx, pKCenter, r, aLeft, k.F);

  const am = aLeft + k.F / 2;
  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#c00000";
  ctx.fillText("F", pKCenter.x + Math.cos(am) * (r + 18), pKCenter.y + Math.sin(am) * (r + 18));
  ctx.restore();
}

export function showSchiebetorSkizzeInNeuemTab(tor: ZElement) {
  if (!tor?.isSchiebetor) {
    throw new Error("showSchiebetorSkizze erwartet ein Schiebetor-Element");
  }

  const torHöhe = tor.size.y;
  const lichteWeite = tor.lichteWeite;
  const k = schiebetorKennzahlen({ lichteWeite, torHöhe });

  const popup = window.open("about:blank", "_blank");
  if (popup?.document?.body) {
    popup.document.title = I18N("Schiebetor-Skizze");
    popup.document.body.innerHTML = buildSketchHtml(tor, k);
    const canvas = popup.document.getElementById("schiebetorSketch") as HTMLCanvasElement | null;
    if (!canvas) {
      throw new Error("Canvas-Element wurde nicht gefunden");
    }
    drawSchiebetorCanvas(canvas, tor);
    return;
  }

  // Popup blockiert: zeichne als Fallback direkt in der aktuellen Seite.
  const existing = document.getElementById("schiebetorSkizzeFallback");
  if (existing) {
    existing.remove();
  }
  const host = document.createElement("div");
  host.id = "schiebetorSkizzeFallback";
  host.style.position = "fixed";
  host.style.inset = "0";
  host.style.zIndex = "99999";
  host.style.background = "rgba(0,0,0,0.5)";
  host.style.overflow = "auto";
  host.innerHTML = `<div style="max-width:1500px;margin:24px auto;background:#fff;padding:16px;">${buildSketchHtml(tor, k, true)}</div>`;
  document.body.appendChild(host);

  const close = host.querySelector("#closeSchiebetorSkizze") as HTMLButtonElement | null;
  close?.addEventListener("click", () => host.remove());
  host.addEventListener("click", (ev) => {
    if (ev.target === host) {
      host.remove();
    }
  });

  const canvas = host.querySelector("#schiebetorSketch") as HTMLCanvasElement | null;
  if (!canvas) {
    throw new Error("Canvas-Element wurde nicht gefunden");
  }

  drawSchiebetorCanvas(canvas, tor);
}

// ─── Fundamentplan ──────────────────────────────────────────────────────────────

/**
 * Transformiert eine Segment-X-Koordinate ins fixPunkt-lokale Koordinatensystem.
 * Im fixPunkt-KS zeigt x=0 immer zum Anschlag/Schloss-Ende, x wächst Richtung Motor.
 * Bei !isAnschlagLinks wird x gespiegelt (wegen der y180-Rotation des fixPunkt).
 */
function segmentXToFixPunktLocal(tor: ZElement, segX: number): number {
  const segment = tor.segment;
  if (!segment) return segX;
  const isFlipped = !tor.isAnschlagLinks;
  const fixPunktSegX = isFlipped ? segment.getX(tor, 1) : segment.getX(tor, 0);
  return isFlipped ? fixPunktSegX - segX : segX - fixPunktSegX;
}

/**
 * Berechnet die Pfosten-Rechtecke im fixPunkt-lokalen Koordinatensystem.
 * Projiziert die tatsächlichen Segment-Positionen der Pfosten ins fixPunkt-KS,
 * sodass Pfosten und Fundamente garantiert im selben Koordinatensystem liegen.
 *
 * Im fixPunkt-KS ist x=0 das Anschlag-Ende (Einzelpfosten, links in der Skizze),
 * x wächst Richtung Motor (Doppelpfosten, rechts in der Skizze).
 */
function berechnePfostenRects(tor: ZElement) {
  const rects: {
    label: string;
    xMin: number;
    xMax: number;
    yMin: number;
    yMax: number;
    zMin: number;
    zMax: number;
  }[] = [];

  const segment = tor.segment;
  const [pfLinks, pfRechts] = tor.getPfosten();
  if ((!pfLinks && !pfRechts) || !segment) return rects;

  for (const pfosten of [pfLinks, pfRechts]) {
    if (pfosten == null) continue;
    const box = pfosten.getBox();
    const isDoppelpfosten = !!pfosten.meta?.Key?.startsWith("Schiebetordoppelpfosten");

    // Pfostenmitte ins fixPunkt-KS projizieren.
    // segment.getX(pfosten, 0.5) = pfosten.position.x (Mitte, unabhängig von breite).
    const centerX = segmentXToFixPunktLocal(tor, segment.getX(pfosten, 0.5));

    // Physische Pfostenbreite: Doppelpfosten fix 0.1m (Pfostenmass.Breite),
    // Einzelpfosten aus BoundingBox.
    const postBreite = isDoppelpfosten ? 0.1 : box.max.x - box.min.x;

    // Doppelpfosten: Innenkante (Torseite) als Referenz nehmen und 0.1m nach außen.
    // Die Positionierungsbreite (0.04m) zentriert den Pfosten nicht auf die physische Mitte,
    // daher 6 cm Korrektur nach links (Richtung Tor).
    const adjustedCenterX = isDoppelpfosten ? centerX - 0.06 : centerX;
    const xMin = adjustedCenterX - postBreite / 2;
    const xMax = adjustedCenterX + postBreite / 2;

    const yMin = box.min.y + pfosten.position.y;
    const yMax = box.max.y + pfosten.position.y;
    const zMin = box.min.z;
    const zMax = box.max.z;

    // Label: Einzelpfosten ist immer "L" (links in der Skizze), Doppelpfosten "R"
    rects.push({
      label: isDoppelpfosten ? I18N("Pfosten R") : I18N("Pfosten L"),
      xMin,
      xMax,
      yMin,
      yMax,
      zMin,
      zMax,
    });
  }
  return rects;
}

/**
 * Gibt den Überstand von Fundament 1 zurück:
 * Abstand zwischen äußerer Pfostenkante (links) und äußerem Fundamentrand.
 * Fundament 1 ist 1.25 m breit und am linken Pfosten zentriert (x = -f1w/2 … +f1w/2).
 */
export function berechneFundament1Überstand(tor: ZElement): number {
  const f1w = 1.25;
  const [pfLinks] = tor.getPfosten();
  if (!pfLinks) return f1w / 2;
  const isDoppelpfosten = pfLinks.isSchiebetorDoppelpfosten;
  const box = pfLinks.getBox();
  const pfostenBreite = isDoppelpfosten ? 0.1 : box.max.x - box.min.x;
  const überstand = f1w / 2 - pfostenBreite / 2;
  return Math.ceil(überstand * 100) / 100;
}

/**
 * Liest die Fundament-Geometrien aus den tatsächlichen 3D-Objekten des fixPunkt.
 * Die Punkte sind bereits im fixPunkt-lokalen Koordinatensystem (x/z-Grundriss).
 * Kein eigenständiges Berechnen oder Spiegeln nötig – die 3D-Objekte enthalten
 * die korrekte Geometrie für die aktuelle Motorseite/Schieberichtung.
 */
export function berechneSchiebetorFundamente(tor: ZElement) {
  const fixPunkt = tor.schiebetorFixPunkt;
  const fund1 = fixPunkt?.["Schiebetor_Fundament1"] as any;
  const fund2 = fixPunkt?.["Schiebetor_Fundament2"] as any;
  const fund3 = fixPunkt?.["Schiebetor_Fundament3"] as any;

  // Fundament-Punkte aus den 3D-Objekten lesen (Vector2: x→x, y→z)
  const readPoints = (fund: any): { x: number; z: number }[] =>
    fund?.points?.map((p: any) => ({ x: p.x, z: p.y })) ?? [];

  const f1Points = readPoints(fund1);
  const f2Points = readPoints(fund2);
  const f3Points = readPoints(fund3);

  const tiefe = fund1?.tiefe ?? 0.7;
  const bodenAbstand = fund1?.bodenAbstand ?? -0.1;

  // Fundament-Abmessungen (bekannte Konstanten)
  const f1w = 1.25;
  const f2w1 = 1.4;
  const f2w2 = 2.338;
  const f2h1 = 0.7;
  const f2h2 = 1.4;
  const f3w = 0.5;

  return {
    fundament1: { points: f1Points, breite: f1w, tiefe, bodenAbstand },
    fundament2: {
      points: f2Points,
      breiteSchmal: f2w1,
      breiteBrut: f2w2,
      tiefeSchmal: f2h1,
      tiefeBrut: f2h2,
      tiefe,
      bodenAbstand,
    },
    fundament3: { points: f3Points, breite: f3w, tiefe, bodenAbstand },
  };
}

function buildFundamentHtml(tor: ZElement, forOverlay = false) {
  const configurationCode = (APP as any)?.configuration?.Code ?? "-";
  const datum = new Date().toLocaleDateString("de-DE");
  const closeButton = forOverlay
    ? `<button id="closeSchiebetorFundament" style="float:right;padding:6px 10px;border:1px solid #999;background:#fff;cursor:pointer;">${I18N("Schliessen")}</button>`
    : "";

  return `
    <style>
      body { font-family: Arial, sans-serif; margin: 16px; color: #222; }
      h2 { margin: 0 0 8px 0; font-size: 18px; }
      .meta { margin: 0 0 8px 0; font-size: 13px; color: #555; }
      .canvasWrap { border: 1px solid #bbb; padding: 8px; margin-bottom: 12px; }
      .canvasWrap h3 { margin: 0 0 6px 0; font-size: 14px; }
    </style>
    ${closeButton}
    <h2>${I18N("Schiebetor-Fundamentplan")}</h2>
    <p class="meta">${I18N("Element")} #${tor.elementNr + 1} &mdash; ${I18N("Code")}: ${configurationCode} &mdash; ${datum}</p>
    <div class="canvasWrap">
      <h3>${I18N("Seitenansicht")}</h3>
      <canvas id="fundamentSide" width="1400" height="500"></canvas>
    </div>
    <div class="canvasWrap">
      <h3>${I18N("Draufsicht")}</h3>
      <canvas id="fundamentTop" width="1400" height="500"></canvas>
    </div>
  `;
}

/**
 * Berechnet die X-Skalierungsparameter der Draufsicht (= Master für die X-Achse).
 * Wird von der Seitenansicht wiederverwendet, damit Fundament-X-Werte in beiden
 * Skizzen auf die gleichen Pixel fallen.
 */
function berechneDraufsichtXScale(tor: ZElement) {
  const fund = berechneSchiebetorFundamente(tor);
  // Die z-Werte aus den 3D-Objekten sind bereits korrekt im fixPunkt-KS –
  // kein zFlip nötig.

  const gespiegeltZ = tor.schiebetormotorseiteAuswahlwert?.Key === "Schiebetormotorseite_B";
  const rahmen = erzeugeSchiebetorRahmen({
    torHöhe: tor.size.y,
    lichteWeite: tor.lichteWeite,
    schiebetorFlügelMaxBreite: tor.meta?.Mass?.SchiebetorFlügelMaxBreite ?? 1.86,
    gespiegeltZ,
  });

  // Rahmen-Offset: Der Rahmen ist im Tor um den GummiPuffer verschoben.
  const gummiKanteScale = rahmen.kanten.find((k: any) => k.name === "SchiebetorGummi");
  const rahmenOffsetXScale = gummiKanteScale?.breite ?? 0.05;

  const rahmenTiefe = 0.05;
  const torXMin = 0 + rahmenOffsetXScale;
  const torXMax = rahmen.torBreite + rahmenOffsetXScale;
  const torZMin = -rahmenTiefe / 2;
  const torZMax = rahmenTiefe / 2;
  const pfostenRects = berechnePfostenRects(tor);

  const allPts = [
    ...fund.fundament1.points,
    ...fund.fundament2.points,
    ...fund.fundament3.points,
    { x: torXMin, z: torZMin },
    { x: torXMax, z: torZMax },
    ...pfostenRects.flatMap((pr) => [
      { x: pr.xMin, z: pr.zMin },
      { x: pr.xMax, z: pr.zMax },
    ]),
  ];
  const minX = Math.min(...allPts.map((p) => p.x)) - 0.15;
  const maxX = Math.max(...allPts.map((p) => p.x)) + 0.15;
  const minZ = Math.min(...allPts.map((p) => p.z)) - 0.15;
  const maxZ = Math.max(...allPts.map((p) => p.z)) + 0.15;

  const padLeft = 90;
  const padRight = 60;
  const padTop = 60;
  const padBottom = 80;
  const canvasW = 1400;
  const canvasH = 500;
  const w = canvasW - padLeft - padRight;
  const h = canvasH - padTop - padBottom;
  const scaleVal = Math.min(w / (maxX - minX), h / (maxZ - minZ));

  return { minX, xScale: scaleVal };
}

/**
 * Zeichnet die Seitenansicht: Tor-Rahmen + Fundamente unterhalb der Bodenlinie.
 */
function drawFundamentSeitenansicht(canvas: HTMLCanvasElement, tor: ZElement) {
  const torHöhe = tor.size.y;
  const lichteWeite = tor.lichteWeite;
  const schiebetorFlügelMaxBreite = tor.meta?.Mass?.SchiebetorFlügelMaxBreite ?? 1.86;
  const gespiegeltZ = tor.schiebetormotorseiteAuswahlwert?.Key === "Schiebetormotorseite_B";

  const rahmen = erzeugeSchiebetorRahmen({
    torHöhe,
    lichteWeite,
    schiebetorFlügelMaxBreite,
    gespiegeltZ,
  });

  const polygonKanten = generateFrame(rahmen);
  const skizzeAusblenden = new Set([
    "profilVertical",
    "profilHorizontal",
    "SchiebetorGummi",
    "FührungsschieneSeite",
  ]);
  const sichtbarePolygonKanten = polygonKanten.filter(
    (pk) => !pk.kante?.name || !skizzeAusblenden.has(pk.kante.name)
  );
  const polygons = sichtbarePolygonKanten.map((pk) => pk.polygon);

  // Rahmen-Offset: Der Rahmen ist im Tor um den GummiPuffer verschoben.
  // Die Rahmen-Polygone starten bei x=0, aber im fixPunkt-KS beginnt der
  // Rahmen erst nach dem GummiPuffer → alle Rahmen-x-Werte nach links verschieben.
  const gummiKante = rahmen.kanten.find((k: any) => k.name === "SchiebetorGummi");
  const rahmenOffsetX = gummiKante?.breite ?? 0.05;

  const fund = berechneSchiebetorFundamente(tor);

  // Fundament-Rechtecke in Seitenansicht (x, y): unter der Bodenlinie (y<0)
  // Fundament 1
  const f1xMin = Math.min(...fund.fundament1.points.map((p) => p.x));
  const f1xMax = Math.max(...fund.fundament1.points.map((p) => p.x));
  // Fundament 2
  const f2xMin = Math.min(...fund.fundament2.points.map((p) => p.x));
  const f2xMax = Math.max(...fund.fundament2.points.map((p) => p.x));
  // Fundament 3
  const f3xMin = Math.min(...fund.fundament3.points.map((p) => p.x));
  const f3xMax = Math.max(...fund.fundament3.points.map((p) => p.x));

  const fundamentTiefe = fund.fundament1.tiefe;

  // Pfosten-Rechtecke berechnen
  const pfostenRects = berechnePfostenRects(tor);

  // Bounding-Box für die gesamte Zeichnung
  const allPolyPts = polygons.flat();
  const minX = Math.min(
    ...allPolyPts.map((p) => p.x + rahmenOffsetX),
    f1xMin,
    f2xMin,
    f3xMin,
    ...pfostenRects.map((pr) => pr.xMin),
    0
  );
  const maxX = Math.max(
    ...allPolyPts.map((p) => p.x + rahmenOffsetX),
    f1xMax,
    f2xMax,
    f3xMax,
    ...pfostenRects.map((pr) => pr.xMax)
  );
  const bodenAbstand = fund.fundament1.bodenAbstand;
  const minY = bodenAbstand - fundamentTiefe - 0.05;
  const posY = tor.positionY;
  const maxY =
    Math.max(
      ...allPolyPts.map((p) => p.y + posY),
      ...pfostenRects.map((pr) => pr.yMax),
      torHöhe + posY
    ) + 0.05;

  const ctx = canvas.getContext("2d")!;
  const padLeft = 90;
  const padTop = 40;
  const padBottom = 70;

  const h = canvas.height - padTop - padBottom;

  // X-Skalierung von der Draufsicht übernehmen (= Master),
  // damit Fundament-X-Werte in beiden Skizzen auf die gleichen Pixel fallen.
  const draufsichtX = berechneDraufsichtXScale(tor);
  const xScale = draufsichtX.xScale;
  const xMinX = draufsichtX.minX;

  // Y-Skalierung eigenständig (nur Höhe anpassen)
  const yScale = h / (maxY - minY);

  const sx = (x: number) => padLeft + (x - xMinX) * xScale;
  const sy = (y: number) => canvas.height - padBottom - (y - minY) * yScale;

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.font = "20px Arial";

  // Bodenlinie
  ctx.save();
  ctx.strokeStyle = "#888";
  ctx.setLineDash([6, 4]);
  ctx.beginPath();
  ctx.moveTo(sx(minX), sy(0));
  ctx.lineTo(sx(maxX), sy(0));
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();

  // Fundamente zeichnen (als Rechtecke unter der Bodenlinie, mit bodenAbstand)
  const drawFundRect = (xMin: number, xMax: number, bodenAbstand: number, color: string) => {
    ctx.save();
    ctx.fillStyle = color;
    ctx.strokeStyle = "#3366aa";
    ctx.lineWidth = 1.4;
    const x0 = sx(xMin);
    const y0 = sy(bodenAbstand);
    const rw = (xMax - xMin) * xScale;
    const rh = fundamentTiefe * yScale;
    ctx.fillRect(x0, y0, rw, rh);
    ctx.strokeRect(x0, y0, rw, rh);
    ctx.restore();
  };

  drawFundRect(f1xMin, f1xMax, fund.fundament1.bodenAbstand, "rgba(180,210,255,0.45)");
  drawFundRect(f2xMin, f2xMax, fund.fundament2.bodenAbstand, "rgba(180,210,255,0.45)");
  drawFundRect(f3xMin, f3xMax, fund.fundament3.bodenAbstand, "rgba(180,210,255,0.45)");

  // Rahmen-Polygone (um positionY nach oben verschoben, um rahmenOffsetX nach links)
  ctx.strokeStyle = "#333";
  ctx.lineWidth = 1.4;
  ctx.fillStyle = "#d9d9d9";
  for (const poly of polygons) {
    if (poly.length < 3) continue;
    ctx.beginPath();
    ctx.moveTo(sx(poly[0].x + rahmenOffsetX), sy(poly[0].y + posY));
    for (let i = 1; i < poly.length; i++) {
      ctx.lineTo(sx(poly[i].x + rahmenOffsetX), sy(poly[i].y + posY));
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }

  // Pfosten als Rechtecke (Seitenansicht: x/y)
  ctx.save();
  ctx.fillStyle = "#c8c8c8";
  ctx.strokeStyle = "#444";
  ctx.lineWidth = 1.4;
  const minPfostenPx = 8;
  for (const pr of pfostenRects) {
    const rwRaw = (pr.xMax - pr.xMin) * xScale;
    const rw = Math.max(rwRaw, minPfostenPx);
    const rx = sx((pr.xMin + pr.xMax) / 2) - rw / 2;
    const ry = sy(pr.yMax);
    const rh = (pr.yMax - pr.yMin) * yScale;
    ctx.fillRect(rx, ry, rw, rh);
    ctx.strokeRect(rx, ry, rw, rh);
  }
  ctx.restore();

  // Rollenböcke und Entlastungsrolle als Rechtecke zwischen Fundament und Tor
  const rollenTeile = [
    { key: "Schiebetor_Rollenbock1" },
    { key: "Schiebetor_Rollenbock2" },
    { key: "Schiebetor_Entlastungsrolle" },
  ];
  ctx.save();
  ctx.fillStyle = "#bbb";
  ctx.strokeStyle = "#666";
  ctx.lineWidth = 1.2;
  for (const teil of rollenTeile) {
    const model =
      tor[teil.key] ??
      tor.schiebetorFixPunkt?.[teil.key] ??
      tor.schiebetorTranslationsPunkt?.[teil.key];
    if (!model) continue;
    // Rechteck von y=posY (Unterkante Tor) bis y=bodenAbstand (Oberkante Fundament)
    const position = model.position;
    const size = model.getSize();
    const rx = sx(position.x - size.x / 2);
    const rw = size.x * xScale;
    const { x } = model.getSize();
    const ry = sy(posY);
    const rh = (posY - bodenAbstand) * yScale;
    ctx.fillRect(rx, ry, rw, rh);
    ctx.strokeRect(rx, ry, rw, rh);
  }
  ctx.restore();

  // Bemaßung: Fundamenttiefe links (Höhe)
  ctx.fillStyle = DIM_COLOR;
  ctx.strokeStyle = DIM_COLOR;

  const dimXLeft = sx(f1xMin) - 40;
  ctx.beginPath();
  ctx.moveTo(sx(f1xMin), sy(bodenAbstand));
  ctx.lineTo(dimXLeft, sy(bodenAbstand));
  ctx.moveTo(sx(f1xMin), sy(bodenAbstand - fundamentTiefe));
  ctx.lineTo(dimXLeft, sy(bodenAbstand - fundamentTiefe));
  ctx.stroke();
  drawDimLine(
    ctx,
    { x: dimXLeft, y: sy(bodenAbstand) },
    { x: dimXLeft, y: sy(bodenAbstand - fundamentTiefe) },
    mToMmText(fundamentTiefe),
    { labelAlign: "right", labelOffsetPx: { x: 35 } }
  );
}

/**
 * Zeichnet die Draufsicht: Fundamente von oben mit Breiten-/Tiefen-Bemaßung.
 */
function drawFundamentDraufsicht(canvas: HTMLCanvasElement, tor: ZElement) {
  const fund = berechneSchiebetorFundamente(tor);
  // Die z-Werte aus den 3D-Objekten sind bereits korrekt im fixPunkt-KS –
  // kein zFlip nötig.

  const gespiegeltZ = tor.schiebetormotorseiteAuswahlwert?.Key === "Schiebetormotorseite_B";

  const rahmen = erzeugeSchiebetorRahmen({
    torHöhe: tor.size.y,
    lichteWeite: tor.lichteWeite,
    schiebetorFlügelMaxBreite: tor.meta?.Mass?.SchiebetorFlügelMaxBreite ?? 1.86,
    gespiegeltZ,
  });

  // Rahmen-Offset: Der Rahmen ist im Tor um den GummiPuffer verschoben.
  const gummiKanteTop = rahmen.kanten.find((k: any) => k.name === "SchiebetorGummi");
  const rahmenOffsetXTop = gummiKanteTop?.breite ?? 0.05;

  // Tor-Rechteck in der Draufsicht (Führungsschiene von oben):
  // x: 0 bis torBreite, z: schmaler Streifen um z=0 (Rahmen-Tiefe 50 mm)
  // Verschiebung um rahmenOffsetX, damit Rahmen im fixPunkt-KS korrekt liegt.
  const rahmenTiefe = 0.05;
  const torXMin = 0 + rahmenOffsetXTop;
  const torXMax = rahmen.torBreite + rahmenOffsetXTop;
  const torZMin = -rahmenTiefe / 2;
  const torZMax = rahmenTiefe / 2;

  // Pfosten-Rechtecke berechnen
  const pfostenRects = berechnePfostenRects(tor);

  // Alle Fundament-Punkte + Tor-Punkte + Pfosten-Punkte sammeln (x/z im Grundriss)
  const allPts = [
    ...fund.fundament1.points,
    ...fund.fundament2.points,
    ...fund.fundament3.points,
    { x: torXMin, z: torZMin },
    { x: torXMax, z: torZMax },
    ...pfostenRects.flatMap((pr) => [
      { x: pr.xMin, z: pr.zMin },
      { x: pr.xMax, z: pr.zMax },
    ]),
  ];
  const minX = Math.min(...allPts.map((p) => p.x)) - 0.15;
  const maxX = Math.max(...allPts.map((p) => p.x)) + 0.15;
  const minZ = Math.min(...allPts.map((p) => p.z)) - 0.15;
  const maxZ = Math.max(...allPts.map((p) => p.z)) + 0.15;

  const ctx = canvas.getContext("2d")!;
  const padLeft = 90;
  const padRight = 60;
  const padTop = 60;
  const padBottom = 80;

  const w = canvas.width - padLeft - padRight;
  const h = canvas.height - padTop - padBottom;
  const scaleVal = Math.min(w / (maxX - minX), h / (maxZ - minZ));

  // x → canvas-x,  z → canvas-y (z nach unten)
  const sx = (x: number) => padLeft + (x - minX) * scaleVal;
  const sz = (z: number) => padTop + (z - minZ) * scaleVal;

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.font = "20px Arial";

  // Mittelachse (Tor-Führungsschiene)
  ctx.save();
  ctx.strokeStyle = "#bbb";
  ctx.setLineDash([8, 4]);
  ctx.beginPath();
  const schieneMitte = 0;
  ctx.moveTo(sx(minX + 0.05), sz(schieneMitte));
  ctx.lineTo(sx(maxX - 0.05), sz(schieneMitte));
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();

  // Tor-Rahmen von oben (Führungsschiene als schmales Rechteck)
  ctx.save();
  ctx.fillStyle = "#d9d9d9";
  ctx.strokeStyle = "#333";
  ctx.lineWidth = 1.4;
  const torRectX = sx(torXMin);
  const torRectZ = sz(torZMin);
  const torRectW = (torXMax - torXMin) * scaleVal;
  const torRectH = (torZMax - torZMin) * scaleVal;
  ctx.fillRect(torRectX, torRectZ, torRectW, torRectH);
  ctx.strokeRect(torRectX, torRectZ, torRectW, torRectH);
  ctx.restore();

  // Pfosten als Rechtecke (Draufsicht: x/z)
  ctx.save();
  ctx.fillStyle = "#c8c8c8";
  ctx.strokeStyle = "#444";
  ctx.lineWidth = 1.4;
  for (const pr of pfostenRects) {
    const rw = (pr.xMax - pr.xMin) * scaleVal;
    const rx = sx((pr.xMin + pr.xMax) / 2) - rw / 2;
    const rz = sz(pr.zMin);
    const rh = (pr.zMax - pr.zMin) * scaleVal;
    ctx.fillRect(rx, rz, rw, rh);
    ctx.strokeRect(rx, rz, rw, rh);
  }
  ctx.restore();

  const torTeileTop = [
    { key: "Schiebetor_Rollenbock1" },
    { key: "Schiebetor_Rollenbock2" },
    { key: "Schiebetor_Entlastungsrolle" },
    { key: "Schiebetor_EAntrieb_Came" },
  ];
  ctx.save();
  ctx.lineWidth = 1;
  for (const teil of torTeileTop) {
    const model =
      tor[teil.key] ??
      tor.schiebetorFixPunkt?.[teil.key] ??
      tor.schiebetorTranslationsPunkt?.[teil.key];
    if (!model) continue;
    const position = model.position;
    const size = model.getSize();
    const rx = sx(position.x - size.x / 2);
    const rz = sz(position.z - size.z / 2);
    const rw = size.x * scaleVal;
    const rh = size.z * scaleVal;
    ctx.fillStyle = "#bbb";
    ctx.strokeStyle = "#666";
    ctx.fillRect(rx, rz, rw, rh);
    ctx.strokeRect(rx, rz, rw, rh);
    console.log(teil, { rx, rz, rw, rh });
  }
  ctx.restore();

  // Fundament-Polygon zeichnen
  const drawFundPoly = (points: { x: number; z: number }[], color: string) => {
    ctx.save();
    ctx.fillStyle = color;
    ctx.strokeStyle = "#3366aa";
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(sx(points[0].x), sz(points[0].z));
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(sx(points[i].x), sz(points[i].z));
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  };

  const fundColor = "rgba(180,210,255,0.45)";
  drawFundPoly(fund.fundament1.points, fundColor);
  drawFundPoly(fund.fundament2.points, fundColor);
  drawFundPoly(fund.fundament3.points, fundColor);

  // ── Bemaßungen ──
  ctx.fillStyle = DIM_COLOR;
  ctx.strokeStyle = DIM_COLOR;

  // Hilfsfunktion: Bounding-Box eines Punkt-Arrays
  const bbox = (pts: { x: number; z: number }[]) => ({
    xMin: Math.min(...pts.map((p) => p.x)),
    xMax: Math.max(...pts.map((p) => p.x)),
    zMin: Math.min(...pts.map((p) => p.z)),
    zMax: Math.max(...pts.map((p) => p.z)),
  });

  // ─ Fundament 1: Breite unten, Tiefe links ─
  const b1 = bbox(fund.fundament1.points);
  const yDimF1Bottom = sz(b1.zMax) + 30;
  ctx.beginPath();
  ctx.moveTo(sx(b1.xMin), sz(b1.zMax));
  ctx.lineTo(sx(b1.xMin), yDimF1Bottom);
  ctx.moveTo(sx(b1.xMax), sz(b1.zMax));
  ctx.lineTo(sx(b1.xMax), yDimF1Bottom);
  ctx.stroke();
  drawDimLine(
    ctx,
    { x: sx(b1.xMin), y: yDimF1Bottom },
    { x: sx(b1.xMax), y: yDimF1Bottom },
    mToMmText(fund.fundament1.breite)
  );

  const xDimF1Left = sx(b1.xMin) - 30;
  ctx.beginPath();
  ctx.moveTo(sx(b1.xMin), sz(b1.zMin));
  ctx.lineTo(xDimF1Left, sz(b1.zMin));
  ctx.moveTo(sx(b1.xMin), sz(b1.zMax));
  ctx.lineTo(xDimF1Left, sz(b1.zMax));
  ctx.stroke();
  drawDimLine(
    ctx,
    { x: xDimF1Left, y: sz(b1.zMin) },
    { x: xDimF1Left, y: sz(b1.zMax) },
    mToMmText(fund.fundament1.breite),
    { labelAlign: "right", labelOffsetPx: { x: 25 } }
  );

  // ─ Fundament 2: Breit-Seite oben, Schmal-Seite unten, Tief-Seite links ─
  const b2 = bbox(fund.fundament2.points);

  // Bestimmen, auf welcher Z-Seite die breite bzw. schmale Kante liegt.
  // points[0]/[1] = breiteBrut-Kante, points[4]/[5] = breiteSchmal-Kante.
  const wideAtZMin = fund.fundament2.points[0].z < fund.fundament2.points[4].z;
  const zBrut = wideAtZMin ? b2.zMin : b2.zMax;
  const zSchmal = wideAtZMin ? b2.zMax : b2.zMin;

  // breiteBrut auf der breiten Seite (Offset weg vom Shape)
  const yDimF2Brut = wideAtZMin ? sz(zBrut) - 30 : sz(zBrut) + 30;
  ctx.beginPath();
  ctx.moveTo(sx(b2.xMin), sz(zBrut));
  ctx.lineTo(sx(b2.xMin), yDimF2Brut);
  ctx.moveTo(sx(b2.xMax), sz(zBrut));
  ctx.lineTo(sx(b2.xMax), yDimF2Brut);
  ctx.stroke();
  drawDimLine(
    ctx,
    { x: sx(b2.xMin), y: yDimF2Brut },
    { x: sx(b2.xMax), y: yDimF2Brut },
    mToMmText(fund.fundament2.breiteBrut)
  );

  // tiefeBrut links
  const xDimF2Left = sx(b2.xMin) - 30;
  ctx.beginPath();
  ctx.moveTo(sx(b2.xMin), sz(b2.zMin));
  ctx.lineTo(xDimF2Left, sz(b2.zMin));
  ctx.moveTo(sx(b2.xMin), sz(b2.zMax));
  ctx.lineTo(xDimF2Left, sz(b2.zMax));
  ctx.stroke();
  drawDimLine(
    ctx,
    { x: xDimF2Left, y: sz(b2.zMin) },
    { x: xDimF2Left, y: sz(b2.zMax) },
    mToMmText(fund.fundament2.tiefeBrut),
    { labelAlign: "right", labelOffsetPx: { x: 25 } }
  );

  // breiteSchmal auf der schmalen Seite
  const f2KnickX = b2.xMin + fund.fundament2.breiteSchmal;
  const f2KnickZ = wideAtZMin
    ? b2.zMin + fund.fundament2.tiefeSchmal
    : b2.zMax - fund.fundament2.tiefeSchmal;
  const yDimF2Schmal = wideAtZMin ? sz(zSchmal) + 30 : sz(zSchmal) - 30;
  ctx.beginPath();
  ctx.moveTo(sx(b2.xMin), sz(zSchmal));
  ctx.lineTo(sx(b2.xMin), yDimF2Schmal);
  ctx.moveTo(sx(f2KnickX), sz(zSchmal));
  ctx.lineTo(sx(f2KnickX), yDimF2Schmal);
  ctx.stroke();
  drawDimLine(
    ctx,
    { x: sx(b2.xMin), y: yDimF2Schmal },
    { x: sx(f2KnickX), y: yDimF2Schmal },
    mToMmText(fund.fundament2.breiteSchmal)
  );

  // Horizontales Maß (breiteBrut − breiteSchmal) auf derselben Höhe, ab L-Knickkante
  ctx.beginPath();
  ctx.moveTo(sx(b2.xMax), sz(f2KnickZ));
  ctx.lineTo(sx(b2.xMax), yDimF2Schmal);
  ctx.stroke();
  drawDimLine(
    ctx,
    { x: sx(f2KnickX), y: yDimF2Schmal },
    { x: sx(b2.xMax), y: yDimF2Schmal },
    mToMmText(fund.fundament2.breiteBrut - fund.fundament2.breiteSchmal)
  );

  // ─ Fundament 3: Breite unten, Tiefe rechts ─
  const b3 = bbox(fund.fundament3.points);
  const yDimF3Bottom = sz(b3.zMax) + 30;
  ctx.beginPath();
  ctx.moveTo(sx(b3.xMin), sz(b3.zMax));
  ctx.lineTo(sx(b3.xMin), yDimF3Bottom);
  ctx.moveTo(sx(b3.xMax), sz(b3.zMax));
  ctx.lineTo(sx(b3.xMax), yDimF3Bottom);
  ctx.stroke();
  drawDimLine(
    ctx,
    { x: sx(b3.xMin), y: yDimF3Bottom },
    { x: sx(b3.xMax), y: yDimF3Bottom },
    mToMmText(fund.fundament3.breite),
    { labelOffsetPx: { y: 20 } }
  );

  const xDimF3Right = sx(b3.xMax) + 30;
  ctx.beginPath();
  ctx.moveTo(sx(b3.xMax), sz(b3.zMin));
  ctx.lineTo(xDimF3Right, sz(b3.zMin));
  ctx.moveTo(sx(b3.xMax), sz(b3.zMax));
  ctx.lineTo(xDimF3Right, sz(b3.zMax));
  ctx.stroke();
  drawDimLine(
    ctx,
    { x: xDimF3Right, y: sz(b3.zMin) },
    { x: xDimF3Right, y: sz(b3.zMax) },
    mToMmText(fund.fundament3.breite),
    { labelAlign: "left", labelOffsetPx: { x: -25 } }
  );
}

export function showSchiebetorFundament(tor: ZElement) {
  if (!tor?.isSchiebetor) {
    throw new Error("showSchiebetorFundament erwartet ein Schiebetor-Element");
  }

  const popup = window.open("about:blank", "_blank");
  if (popup?.document?.body) {
    popup.document.title = I18N("Schiebetor-Fundamentplan");
    popup.document.body.innerHTML = buildFundamentHtml(tor);

    const sideCanvas = popup.document.getElementById("fundamentSide") as HTMLCanvasElement | null;
    const topCanvas = popup.document.getElementById("fundamentTop") as HTMLCanvasElement | null;

    if (sideCanvas) drawFundamentSeitenansicht(sideCanvas, tor);
    if (topCanvas) drawFundamentDraufsicht(topCanvas, tor);
    return;
  }

  // Popup blockiert: Fallback als Overlay
  const existing = document.getElementById("schiebetorFundamentFallback");
  if (existing) existing.remove();

  const host = document.createElement("div");
  host.id = "schiebetorFundamentFallback";
  host.style.position = "fixed";
  host.style.inset = "0";
  host.style.zIndex = "99999";
  host.style.background = "rgba(0,0,0,0.5)";
  host.style.overflow = "auto";
  host.innerHTML = `<div style="max-width:1500px;margin:24px auto;background:#fff;padding:16px;">${buildFundamentHtml(tor, true)}</div>`;
  document.body.appendChild(host);

  const close = host.querySelector("#closeSchiebetorFundament") as HTMLButtonElement | null;
  close?.addEventListener("click", () => host.remove());
  host.addEventListener("click", (ev) => {
    if (ev.target === host) host.remove();
  });

  const sideCanvas = host.querySelector("#fundamentSide") as HTMLCanvasElement | null;
  const topCanvas = host.querySelector("#fundamentTop") as HTMLCanvasElement | null;

  if (sideCanvas) drawFundamentSeitenansicht(sideCanvas, tor);
  if (topCanvas) drawFundamentDraufsicht(topCanvas, tor);
}

export async function createSchiebetorSkizzeBlob(tor: ZElement): Promise<Blob> {
  if (!tor?.isSchiebetor) {
    throw new Error("createSchiebetorSkizzeBlob erwartet ein Schiebetor-Element");
  }

  const canvas = document.createElement("canvas");
  canvas.width = 1400;
  canvas.height = 640;
  drawSchiebetorCanvas(canvas, tor);

  return canvasToBlob(canvas);
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error("canvas.toBlob lieferte null"));
      }
    }, "image/png");
  });
}

export async function createSchiebetorFundamentBlobs(
  tor: ZElement
): Promise<{ seitenansicht: Blob; draufsicht: Blob }> {
  if (!tor?.isSchiebetor) {
    throw new Error("createSchiebetorFundamentBlobs erwartet ein Schiebetor-Element");
  }

  const sideCanvas = document.createElement("canvas");
  sideCanvas.width = 1400;
  sideCanvas.height = 500;
  drawFundamentSeitenansicht(sideCanvas, tor);

  const topCanvas = document.createElement("canvas");
  topCanvas.width = 1400;
  topCanvas.height = 500;
  drawFundamentDraufsicht(topCanvas, tor);

  const [seitenansicht, draufsicht] = await Promise.all([
    canvasToBlob(sideCanvas),
    canvasToBlob(topCanvas),
  ]);

  return { seitenansicht, draufsicht };
}
